import { supabase } from "@/lib/supabase";
import type { CashClosing, CashMovement, CashRegister, CashSummary } from "@/types";

const REGISTER_COLUMNS = "id, branch_id, user_id, opening_amount, status, opened_at, closed_at";

export interface OpenRegisterResult {
  ok: boolean;
  error: string | null;
  register?: CashRegister;
}

/** Returns the currently open register for a user at a branch, if any. */
export async function fetchOpenRegister(
  userId: string,
  branchId: string | null,
): Promise<CashRegister | null> {
  try {
    let query = supabase
      .from("cash_registers")
      .select(REGISTER_COLUMNS)
      .eq("user_id", userId)
      .eq("status", "open")
      .order("opened_at", { ascending: false })
      .limit(1);
    if (branchId) query = query.eq("branch_id", branchId);
    const { data, error } = await query.maybeSingle();
    if (error || !data) return null;
    return data as CashRegister;
  } catch {
    return null;
  }
}

export async function openCashRegister(
  branchId: string,
  userId: string,
  openingAmount: number,
): Promise<OpenRegisterResult> {
  try {
    const { data, error } = await supabase
      .from("cash_registers")
      .insert({
        branch_id: branchId,
        user_id: userId,
        opening_amount: openingAmount,
        status: "open",
        opened_at: new Date().toISOString(),
      })
      .select(REGISTER_COLUMNS)
      .maybeSingle();
    if (error || !data) {
      return { ok: false, error: "No se pudo abrir la caja." };
    }
    return { ok: true, error: null, register: data as CashRegister };
  } catch {
    return { ok: false, error: "Ocurrió un error inesperado al abrir la caja." };
  }
}

export async function fetchCashSummary(registerId: string): Promise<CashSummary | null> {
  try {
    const { data, error } = await supabase.rpc("rkc_cash_summary", { p_register_id: registerId });
    if (error || !data) return null;
    return data as CashSummary;
  } catch {
    return null;
  }
}

export interface CloseRegisterResult {
  ok: boolean;
  error: string | null;
  expected?: number;
  counted?: number;
  difference?: number;
}

export async function closeCashRegister(
  registerId: string,
  counted: number,
  breakdown: Record<string, unknown>,
): Promise<CloseRegisterResult> {
  try {
    const { data, error } = await supabase.rpc("rkc_close_cash_register", {
      p_register_id: registerId,
      p_counted: counted,
      p_breakdown: breakdown,
    });
    if (error || !data) {
      return { ok: false, error: error?.message || "No se pudo cerrar la caja." };
    }
    const result = data as { expected_total?: number; counted_total?: number; difference?: number };
    return {
      ok: true,
      error: null,
      expected: Number(result.expected_total ?? 0),
      counted: Number(result.counted_total ?? 0),
      difference: Number(result.difference ?? 0),
    };
  } catch {
    return { ok: false, error: "Ocurrió un error inesperado al cerrar la caja." };
  }
}

export async function addCashMovement(
  registerId: string,
  userId: string,
  movementType: "ingreso" | "egreso",
  amount: number,
  concept: string,
): Promise<{ ok: boolean; error: string | null }> {
  try {
    const { error } = await supabase.from("cash_movements").insert({
      register_id: registerId,
      user_id: userId,
      movement_type: movementType,
      amount,
      concept,
    });
    if (error) return { ok: false, error: "No se pudo registrar el movimiento." };
    return { ok: true, error: null };
  } catch {
    return { ok: false, error: "Ocurrió un error inesperado al registrar el movimiento." };
  }
}

export async function loadCashMovements(registerId: string): Promise<CashMovement[]> {
  try {
    const { data, error } = await supabase
      .from("cash_movements")
      .select("id, register_id, movement_type, amount, concept, user_id, created_at")
      .eq("register_id", registerId)
      .order("created_at", { ascending: false });
    if (error) return [];
    const rows = (data ?? []) as CashMovement[];
    const userIds = Array.from(new Set(rows.map((r) => r.user_id).filter(Boolean))) as string[];
    if (userIds.length === 0) return rows;

    const { data: profiles } = await supabase
      .from("profiles")
      .select("id, full_name, last_name")
      .in("id", userIds);
    const nameMap = new Map(
      (profiles ?? []).map((p: { id: string; full_name: string; last_name: string }) => [
        p.id,
        `${p.full_name} ${p.last_name}`.trim(),
      ]),
    );
    return rows.map((row) => ({
      ...row,
      user_name: row.user_id ? nameMap.get(row.user_id) ?? null : null,
    }));
  } catch {
    return [];
  }
}

export interface RegistersData {
  registers: CashRegister[];
  error: string | null;
}

export async function loadCashRegisters(): Promise<RegistersData> {
  try {
    const { data, error } = await supabase
      .from("cash_registers")
      .select(REGISTER_COLUMNS)
      .order("opened_at", { ascending: false })
      .limit(300);
    if (error) return { registers: [], error: "No se pudieron cargar las cajas." };
    const rows = (data ?? []) as CashRegister[];

    const branchIds = Array.from(new Set(rows.map((r) => r.branch_id).filter(Boolean))) as string[];
    const userIds = Array.from(new Set(rows.map((r) => r.user_id).filter(Boolean))) as string[];

    const [branchesRes, profilesRes] = await Promise.all([
      branchIds.length
        ? supabase.from("branches").select("id, name").in("id", branchIds)
        : Promise.resolve({ data: [] as { id: string; name: string }[] }),
      userIds.length
        ? supabase.from("profiles").select("id, full_name, last_name").in("id", userIds)
        : Promise.resolve({ data: [] as { id: string; full_name: string; last_name: string }[] }),
    ]);

    const branchMap = new Map((branchesRes.data ?? []).map((b: { id: string; name: string }) => [b.id, b.name]));
    const userMap = new Map(
      (profilesRes.data ?? []).map((p: { id: string; full_name: string; last_name: string }) => [
        p.id,
        `${p.full_name} ${p.last_name}`.trim(),
      ]),
    );

    return {
      registers: rows.map((row) => ({
        ...row,
        branch_name: row.branch_id ? branchMap.get(row.branch_id) ?? null : null,
        user_name: row.user_id ? userMap.get(row.user_id) ?? null : null,
      })),
      error: null,
    };
  } catch {
    return { registers: [], error: "No se pudieron cargar las cajas." };
  }
}

export interface ClosingsData {
  closings: CashClosing[];
  error: string | null;
}

export async function loadCashClosings(): Promise<ClosingsData> {
  try {
    const { data, error } = await supabase
      .from("cash_closings")
      .select("id, register_id, branch_id, user_id, expected_total, counted_total, difference, breakdown, closed_at")
      .order("closed_at", { ascending: false })
      .limit(200);
    if (error) return { closings: [], error: "No se pudieron cargar los cierres." };
    const rows = (data ?? []) as CashClosing[];

    const branchIds = Array.from(new Set(rows.map((r) => r.branch_id).filter(Boolean))) as string[];
    const userIds = Array.from(new Set(rows.map((r) => r.user_id).filter(Boolean))) as string[];
    const registerIds = Array.from(new Set(rows.map((r) => r.register_id).filter(Boolean))) as string[];

    const [branchesRes, profilesRes, registersRes] = await Promise.all([
      branchIds.length
        ? supabase.from("branches").select("id, name").in("id", branchIds)
        : Promise.resolve({ data: [] as { id: string; name: string }[] }),
      userIds.length
        ? supabase.from("profiles").select("id, full_name, last_name").in("id", userIds)
        : Promise.resolve({ data: [] as { id: string; full_name: string; last_name: string }[] }),
      registerIds.length
        ? supabase.from("cash_registers").select("id, opening_amount, opened_at").in("id", registerIds)
        : Promise.resolve({ data: [] as { id: string; opening_amount: number | null; opened_at: string | null }[] }),
    ]);

    const branchMap = new Map((branchesRes.data ?? []).map((b: { id: string; name: string }) => [b.id, b.name]));
    const userMap = new Map(
      (profilesRes.data ?? []).map((p: { id: string; full_name: string; last_name: string }) => [
        p.id,
        `${p.full_name} ${p.last_name}`.trim(),
      ]),
    );
    const registerMap = new Map(
      (registersRes.data ?? []).map(
        (r: { id: string; opening_amount: number | null; opened_at: string | null }) => [r.id, r],
      ),
    );

    return {
      closings: rows.map((row) => {
        const reg = row.register_id ? registerMap.get(row.register_id) : undefined;
        return {
          ...row,
          branch_name: row.branch_id ? branchMap.get(row.branch_id) ?? null : null,
          user_name: row.user_id ? userMap.get(row.user_id) ?? null : null,
          opening_amount: reg?.opening_amount ?? null,
          opened_at: reg?.opened_at ?? null,
        };
      }),
      error: null,
    };
  } catch {
    return { closings: [], error: "No se pudieron cargar los cierres." };
  }
}